import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageHeader from '../../../components/common/PageHeader/PageHeader';
import './ReleaseNotes.css';
import InputBoxSmall from '../../../components/common/InputBox/InputBoxSmall';
import { publishReleaseNote, deleteReleaseNote } from '../../../services/ReleaseNotes/Releasenotesapi';
import IconUploadButton from '../../../components/common/InputBox/UploadIconBox';
import { SuccessAlert } from '../../../components/common/AlertBox/GreenAlert';
import DeletePopup from '../../../components/Popup/DeletePopup/DeletePopup';
import { showAlert } from '../../../components/common/ExportLabel/Exporterror';


const ReleaseNotesDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isPublishing, setIsPublishing] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleDelete = async () => {
        if (isDeleting) return;
        setIsDeleting(true);
        try {
            await deleteReleaseNote(note.id);
            navigate('/release-notes');
            SuccessAlert('Release note deleted successfully');
        } catch (error) {
            console.error(error.message);
        } finally {
            setIsDeleting(false);
        }
    };

    const note = location.state?.note;

    if (!note) {
        return (
            <div className='whole-container'>
                <div className='pagename-container'>
                    <span className='common-text fts-16'>No release note data found.</span>
                    <button className='cancel-btn-rn bg-white' onClick={() => navigate('/release-notes')}>
                        Cancel
                    </button>
                </div>
            </div>
        );
    }

    const { version, title, release_date, status, features, improvements, bug_fixes } = note;

    // ── Publish handler ───────────────────────────────────────────
    const handlePublish = async () => {

        if (note.release_date) {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            let selected = new Date(note.release_date);
            const match = note.release_date.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (match) {
                selected = new Date(parseInt(match[1]), parseInt(match[2]) - 1, parseInt(match[3]));
            }
            selected.setHours(0, 0, 0, 0);

            if (selected > today) {
                showAlert('Cannot publish a release note with a future date. Save as draft instead.');
                return;
            }
        }

        if (isPublishing) return;
        setIsPublishing(true);
        try {
            const result = await publishReleaseNote(note.id);
            if (result) {
                SuccessAlert('Release note published successfully!');
                navigate('/release-notes');
            }
        }
        catch (error) {
            // plug in your showAlert component here if you have one
            console.error(error.message);
        } finally {
            setIsPublishing(false);
        }
    };
    const capitalize = (text) =>
        text ? text.charAt(0).toUpperCase() + text.slice(1) : '';

    const formatDate = (dateString) => {
        if (!dateString) return '';

        const [year, month, day] = dateString.split('-');
        return `${day}-${month}-${year}`;
    };

    return (
        <div className='whole-container'>
            <div className='top-heading'>
                <PageHeader
                    pageName=" Engagement / Release Notes /"
                    nestedpageName="Details"
                    linkTo="/release-notes"
                />
            </div>
            <div className='pagename-container'>
                <div className='page-name'>
                    <h1 className='common-text fts-22 ftw-6 blue'>Release Note Details</h1>
                </div>
            </div>
            <div className='table-container'>
                <div className='add-release-note-container'>
                    <div className='release-basic-container'>
                        <div className='release-basic-header'>
                            <span className='release-header-title'>Basic Information</span>
                        </div>
                        <div className='release-basic-content'>
                            <div className='release-version-container'>
                                <InputBoxSmall label="Version" defaultValue={version} readOnly />
                            </div>
                            <div className='release-basic-description-container'>
                                <InputBoxSmall label="Release title" defaultValue={title} readOnly />
                                <InputBoxSmall label="Release date" defaultValue={formatDate(release_date)} readOnly />
                                <InputBoxSmall label="Status" defaultValue={capitalize(status)} readOnly />
                            </div>
                            <div className='release-basic-bt-text'>
                                <span>This title is for internal use only and will not be shown to users.</span>
                            </div>
                        </div>
                    </div>
                    {/* ── FEATURE INFO ───────────────────────────────────────── */}
                    <div className='release-feature-container'>
                        <div className='release-feature-header'>
                            <span className='release-header-title'>New Features</span>
                        </div>
                        <div className='release-feature-content-container'>
                            <div className='release-feature-titles release-feature-grid'>
                                <span>Icon</span>
                                <span>Feature Name</span>
                                <span>Description</span>
                                <span></span>
                            </div>
                            <div className='release-feature-content'>
                                {features?.length > 0 ? features.map((item) => (
                                    <div className='release-feature-row release-feature-grid' key={item.id}>
                                        <IconUploadButton icon={item.image} readOnly />
                                        <div className='release-box-item' title={item.name}>{item.name}</div>
                                        <div className='release-box-item' title={item.description}>{item.description}</div>
                                        <div></div>
                                    </div>
                                )) : (
                                    <p className='common-text fts-14' style={{ padding: '8px 0', color: '#888' }}>No features added.</p>
                                )}
                            </div>
                        </div>

                        <hr />

                        {/* ── IMPROVEMENTS ────────────────────────────── */}
                        <div className='release-feature-container' style={{ marginTop: '1.5rem' }}>
                            <div className='release-feature-header'>
                                <span className='release-header-title'>Improvements</span>
                            </div>
                            <div className='release-feature-content-container'>
                                <div className='release-feature-titles release-improvement-grid'>
                                    <span>Improvement Title</span>
                                    <span>Description</span>
                                    <span></span>
                                </div>
                                <div className='release-feature-content'>
                                    {improvements?.length > 0 ? improvements.map((item) => (
                                        <div className='release-feature-row release-improvement-grid' key={item.id}>
                                            <div className='release-box-item' title={item.name || item.text}>{item.name || item.text}</div>
                                            <div className='release-box-item' title={item.description}>{item.description || '—'}</div>
                                            <div></div>
                                        </div>
                                    )) : (
                                        <p className='common-text fts-14' style={{ padding: '8px 0', color: '#888' }}>No improvements added.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── BUG FIXES ───────────────────────────────── */}
                        <div className='release-feature-container' style={{ marginTop: '1.5rem' }}>
                            <div className='release-feature-header'>
                                <span className='release-header-title'>Bug Fixes</span>
                            </div>
                            <div className='release-feature-content-container'>
                                <div className='release-feature-titles release-improvement-grid'>
                                    <span>Bug Title</span>
                                    <span>Description</span>
                                    <span></span>
                                </div>
                                <div className='release-feature-content'>
                                    {bug_fixes?.length > 0 ? bug_fixes.map((item) => (
                                        <div className='release-feature-row release-improvement-grid' key={item.id}>
                                            <div className='release-box-item' title={item.name || item.text}>{item.name || item.text}</div>
                                            <div className='release-box-item' title={item.description}>{item.description || '—'}</div>
                                            <div></div>
                                        </div>
                                    )) : (
                                        <p className='common-text fts-14' style={{ padding: '8px 0', color: '#888' }}>No bug fixes added.</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── ACTION BUTTONS ─────────────────────────────── */}
                        <div className='release-final-btn-container'>
                            <button
                                className='cancel-btn-rn bg-white'
                                onClick={() => navigate('/release-notes')}
                            >
                                Cancel
                            </button>
                            <button
                                className='delete-btn-rn'
                                onClick={() => setIsDeletePopupOpen(true)}
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" className='trash-can-red'>
                                    <path d="M2.5 5H17.5" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M15.8334 5V16.6667C15.8334 17.5 15.0001 18.3333 14.1667 18.3333H5.83341C5.00008 18.3333 4.16675 17.5 4.16675 16.6667V5" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M6.66675 5.00033V3.33366C6.66675 2.50033 7.50008 1.66699 8.33341 1.66699H11.6667C12.5001 1.66699 13.3334 2.50033 13.3334 3.33366V5.00033" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M8.33325 9.16699V14.167" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M11.6667 9.16699V14.167" stroke="#FF0000" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                                </svg>
                                Delete
                            </button>
                            <button
                                className='save-draft-btn-rn'
                                onClick={() => navigate(`/release-notes/edit/${note.id}`, { state: { note } })}
                            >
                                Edit
                            </button>
                            {status !== 'published' && (
                                <button
                                    className='release-final-btn'
                                    onClick={handlePublish}
                                    disabled={isPublishing}
                                >
                                    {isPublishing ? 'Publishing...' : 'Publish Release Note'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <DeletePopup
                isOpen={isDeletePopupOpen}
                title="Delete Release Note"
                fields={[{ label: 'Are you sure you want to delete this release note? Once deleted, it cannot be recovered.', type: 'text' }]}
                onSave={handleDelete}
                onClose={() => setIsDeletePopupOpen(false)}
            />
        </div>
    );
};

export default ReleaseNotesDetail;